import { useEffect, useRef, useState } from "react";
import { FaCloudArrowUp } from "react-icons/fa6";
import { ev } from "../../lib/everythingCopy";

// Files ≤ 50 MB go through Telegram (Bot API cap); larger files route to
// Backblaze B2 via a presigned PUT URL (browser uploads direct, bypassing
// Cloudflare Workers' 100 MB body cap). B2 itself caps a single object at
// 5 GB on the S3-compatible API — that's our practical client-side ceiling.
export const MAX_BYTES = 5 * 1024 * 1024 * 1024;

export async function probeDimensions(file) {
  if (file.type.startsWith("image/")) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({});
      };
      img.src = url;
    });
  }
  if (file.type.startsWith("video/")) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve({ width: video.videoWidth, height: video.videoHeight });
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({});
      };
      video.src = url;
    });
  }
  return {};
}

export function UploadZone({ onFiles, inputRef }) {
  const [dragging, setDragging] = useState(false);
  const counterRef = useRef(0);

  useEffect(() => {
    function onDragEnter(e) {
      if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes("Files")) return;
      counterRef.current++;
      setDragging(true);
    }
    function onDragLeave() {
      counterRef.current = Math.max(0, counterRef.current - 1);
      if (counterRef.current === 0) setDragging(false);
    }
    function onDragOver(e) { e.preventDefault(); }
    function onDrop(e) {
      e.preventDefault();
      counterRef.current = 0;
      setDragging(false);
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length) onFiles(files);
    }
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
    };
  }, [onFiles]);

  function onPick(e) {
    const files = Array.from(e.target.files || []);
    if (files.length) onFiles(files);
    e.target.value = "";
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        onChange={onPick}
        className="hidden"
      />
      {dragging && (
        <div className="fixed inset-0 z-40 pointer-events-none flex items-center justify-center p-8">
          <div className="absolute inset-4 rounded-3xl border-2 border-dashed border-zinc-400 dark:border-zinc-500 bg-zinc-50/40 dark:bg-zinc-900/40 backdrop-blur-sm" />
          <div className="relative flex flex-col items-center gap-3 text-zinc-700 dark:text-zinc-200">
            <FaCloudArrowUp className="w-12 h-12" />
            <p className="text-lg font-semibold">{ev.dropToUpload}</p>
            <p className="text-xs">{ev.maxFileSize}</p>
          </div>
        </div>
      )}
    </>
  );
}
